import Link from 'next/link';
import React, { useState } from 'react';
import { Accordion, Card, Col, Row } from 'react-bootstrap';
import AccordionIcon from '../components/AccordionIcon';
import Head from '../components/head';
import Layout from '../components/Layout';
import OpenGraphDescription from '../components/OpenGraphDescription';
import OpenGraphTitle from '../components/OpenGraphTitle';
import PageTitle from '../components/PageTitle';
import Section from '../components/Section';
import SwooptyBg from '../components/SwooptyBg';
import { FranchiseeProvider } from '../context/Franchisee';
import PhoneLink from '../components/PhoneLink';
import defaultFranchisee from '../context/Franchisee/defaultFranchisee.json'
// import SVG from "react-inlinesvg"

interface FaqsPageProps {
  /* add faqs page props here */
  isComponent?: boolean;
}

const Faqs: React.FC<FaqsPageProps> = ({ isComponent }: FaqsPageProps) => {
  // control toggle icon on header
  const [activeEventKey, setActiveEventKey] = useState('-1');
  function toggleEventKey(eventKey: string): void {
    if (activeEventKey === eventKey) {
      setActiveEventKey('-1');
    } else {
      setActiveEventKey(eventKey);
    }
  }
  return (
    <FranchiseeProvider>
      {isComponent ? (
        <>
          <Section lg={12} xs={10}>
            {!isComponent && <PageTitle className='mb-0'>FAQs</PageTitle>}
          </Section>
          <Section padding='bottom' lg={12} xs={10}>
            <Accordion className='accordion__card'>
              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='0'
                  onClick={() => toggleEventKey('0')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Do you offer different cleaning packages and costs?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='0'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='0'>
                  <Card.Body>
                    <p>
                      Yes, we have a variety of package options. Whether you want a one-time clean or a recurring professional cleaning service, we have 
                      the right package for you! <a href='/services' target='_blank'>Click here to view more details</a>.
                    </p>
                    {/* <Link href='/services/'>
                      <a className='btn btn-outline-primary'>
                        View Our Services
                      </a>
                    </Link> */}
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='1'
                  onClick={() => toggleEventKey('1')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What if I'm not happy with my cleaning?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='1'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='1'>
                  <Card.Body>
                    <p>
                      Your satisfaction is important to us. Your feedback is crucial to our growth and helps to ensure the delivery of high-quality service. 
                      You will receive a text message within 24 hours of your cleaning, inviting you to rate the service and share your feedback. If you require 
                      immediate assistance, please feel free to contact our office at <PhoneLink number={defaultFranchisee.phonePrimary} />, or contact your local branch listed in our <a href='/locations'>directory</a>. If you are unsatisfied, please notify us within 48 hours 
                      of your cleaning and we will promptly schedule a free re-clean to address your specific concerns.
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='2'
                  onClick={() => toggleEventKey('2')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What about cleaning supplies?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='2'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='2'>
                  <Card.Body>
                    <p>
                      At Two Maids, we provide all supplies and equipment needed to complete your cleaning package. If you prefer, 
                      we use your product, please leave the product on your kitchen counter and communicate with the office about 
                      your preferences. Products must have a store-bought label with warnings and ingredients. We do not offer a 
                      discount to use customer-provided supplies.
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='3'
                  onClick={() => toggleEventKey('3')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What do I need to do before you come to my home?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='3'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='3'>
                  <Card.Body>
                    <p>
                      The best thing you can do is minimize clutter prior to your cleaning. The more surfaces we can access the more value you will get out of 
                      your clean. If you are scheduled for a Premium or a Deep Clean, leave sheets out on the beds that you would like changed.  
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='4'
                  onClick={() => toggleEventKey('4')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Can my pet stay home during the clean? 
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='4'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='4'>
                  <Card.Body>
                    <p>
                      Yes - find a good spot for your pets so they’re safe and secure while we clean. As much as we love your furry friends, please pick up any animal feces prior to your cleaning.   
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='5'
                  onClick={() => toggleEventKey('5')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What if I need to reschedule? 
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='5'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='5'>
                  <Card.Body>
                    <p>
                      Life Happens. We get it. Please let us know two business days prior to your scheduled appointment and we will do our best to work you into another day/time. Last minute cancellations 
                      may incur a fee according to your local Two Maids location policies.     
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='6'
                  onClick={() => toggleEventKey('6')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Will you send me the same team every time?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='6'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='6'>
                  <Card.Body>
                    <p>
                      Providing consistent and quality cleaning services is a high priority for us. We strive to send a familiar face, and if one is unavailable, we will find a fitting replacement. All PHC’s 
                      are thoroughly trained to provide consistent quality to our customers. Let us know your favorites!     
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='7'
                  onClick={() => toggleEventKey('7')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Do I have to be home when you come clean?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='7'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='7'>
                  <Card.Body>
                    <p>
                      Nope! It is not necessary for you to be present while your
                      team of professional house cleaners provides your cleaning
                      service. In fact, the majority of our customers are not at
                      home while we clean. You can leave a spare key for us that
                      we will keep in a lock box or provide a garage or door
                      code.
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='8'
                  onClick={() => toggleEventKey('8')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Do you offer franchising?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='8'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='8'>
                  <Card.Body>
                    <p>
                      Yes, we do! We currently have over 100+ Two Maids locations across the nation. For more information on
                      opening a Two Maids franchise near you, visit our{' '}
                      <a
                        href='https://twomaidsfranchise.com/'
                        target='_blank'
                        rel='noreferrer'
                      >
                        franchising website
                      </a>
                      .
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='9'
                  onClick={() => toggleEventKey('9')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What if my clean falls on a holiday? 
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='9'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='9'>
                  <Card.Body>
                    <p>
                      We will reach out to you if your regularly scheduled clean falls on a holiday.  
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='10'
                  onClick={() => toggleEventKey('10')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Do your Professional House Cleaners accept tips?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='10'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='10'>
                  <Card.Body>
                    <p>
                      Our PHC’s are allowed to accept tips. While tipping is not expected it is always appreciated!
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='11'
                  onClick={() => toggleEventKey('11')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What if someone gets hurt in my home? 
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='11'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='11'>
                  <Card.Body>
                    <p>
                      Two Maids is fully licensed, bonded, and insured allowing you peace of mind. Any on the job injuries will be covered by the claim.
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='12'
                  onClick={() => toggleEventKey('12')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What if something is broken during my cleaning?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='12'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='12'>
                  <Card.Body>
                    <p>
                      At Two Maids, your home will be treated with the utmost care. In the event of an accident occurring, we will do our best to repair or replace 
                      and the damage we cause. All our PHC’s are fully licensed, bonded, and insured to protect you against any worse case scenarios.
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          </Section>
          <Section />
        </>
      ) : (
        <Layout alternateFooter>
          <Head
            slug='faqs'
            title='FAQs - Two Maids Maid Cleaning Services'
            description='Questions and answers from Two Maids, with information about our cleaning packages and policies.'
          />
          <OpenGraphTitle title='FAQs - Two Maids Maid Cleaning Services' />
          <OpenGraphDescription>
            Questions and answers from Two Maids, with information about our cleaning packages and policies.
          </OpenGraphDescription>
          <Section padding='top'>
            <PageTitle className='mb-0'>Frequently Asked Questions</PageTitle>
          </Section>
          <Section padding='bottom'>
          <Accordion className='accordion__card'>
              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='0'
                  onClick={() => toggleEventKey('0')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Do you offer different cleaning packages and costs?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='0'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='0'>
                  <Card.Body>
                    <p>
                      Yes, we have a variety of package options. Whether you want a one-time clean or a recurring professional cleaning service, we have 
                      the right package for you! <a href='/services' target='_blank'>Click here to view more details</a>.
                    </p>
                    {/* <Link href='/services/'>
                      <a className='btn btn-outline-primary'>
                        View Our Services
                      </a>
                    </Link> */}
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='1'
                  onClick={() => toggleEventKey('1')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What if I'm not happy with my cleaning?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='1'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='1'>
                  <Card.Body>
                    <p>
                      Your satisfaction is important to us. Your feedback is crucial to our growth and helps to ensure the delivery of high-quality service. 
                      You will receive a text message within 24 hours of your cleaning, inviting you to rate the service and share your feedback. If you require 
                      immediate assistance, please feel free to contact our office at <PhoneLink number={defaultFranchisee.phonePrimary} />, or contact your local branch listed in our <a href='/locations'>directory</a>. If you are unsatisfied, please notify us within 48 hours 
                      of your cleaning and we will promptly schedule a free re-clean to address your specific concerns.
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='2'
                  onClick={() => toggleEventKey('2')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What about cleaning supplies?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='2'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='2'>
                  <Card.Body>
                    <p>
                      At Two Maids, we provide all supplies and equipment needed to complete your cleaning package. If you prefer, 
                      we use your product, please leave the product on your kitchen counter and communicate with the office about 
                      your preferences. Products must have a store-bought label with warnings and ingredients. We do not offer a 
                      discount to use customer-provided supplies.
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='3'
                  onClick={() => toggleEventKey('3')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What do I need to do before you come to my home?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='3'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='3'>
                  <Card.Body>
                    <p>
                      The best thing you can do is minimize clutter prior to your cleaning. The more surfaces we can access the more value you will get out of 
                      your clean. If you are scheduled for a Premium or a Deep Clean, leave sheets out on the beds that you would like changed.  
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='4'
                  onClick={() => toggleEventKey('4')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Can my pet stay home during the clean? 
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='4'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='4'>
                  <Card.Body>
                    <p>
                      Yes - find a good spot for your pets so they’re safe and secure while we clean. As much as we love your furry friends, please pick up any animal feces prior to your cleaning.   
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='5'
                  onClick={() => toggleEventKey('5')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What if I need to reschedule? 
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='5'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='5'>
                  <Card.Body>
                    <p>
                      Life Happens. We get it. Please let us know two business days prior to your scheduled appointment and we will do our best to work you into another day/time. Last minute cancellations 
                      may incur a fee according to your local Two Maids location policies.     
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='6'
                  onClick={() => toggleEventKey('6')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Will you send me the same team every time?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='6'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='6'>
                  <Card.Body>
                    <p>
                      Providing consistent and quality cleaning services is a high priority for us. We strive to send a familiar face, and if one is unavailable, we will find a fitting replacement. All PHC’s 
                      are thoroughly trained to provide consistent quality to our customers. Let us know your favorites!     
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='7'
                  onClick={() => toggleEventKey('7')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Do I have to be home when you come clean?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='7'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='7'>
                  <Card.Body>
                    <p>
                      Nope! It is not necessary for you to be present while your
                      team of professional house cleaners provides your cleaning
                      service. In fact, the majority of our customers are not at
                      home while we clean. You can leave a spare key for us that
                      we will keep in a lock box or provide a garage or door
                      code.
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='8'
                  onClick={() => toggleEventKey('8')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Do you offer franchising?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='8'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='8'>
                  <Card.Body>
                    <p>
                      Yes, we do! We currently have over 100+ Two Maids locations across the nation. For more information on
                      opening a Two Maids franchise near you, visit our{' '}
                      <a
                        href='https://twomaidsfranchise.com/'
                        target='_blank'
                        rel='noreferrer'
                      >
                        franchising website
                      </a>
                      .
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='9'
                  onClick={() => toggleEventKey('9')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What if my clean falls on a holiday? 
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='9'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='9'>
                  <Card.Body>
                    <p>
                      We will reach out to you if your regularly scheduled clean falls on a holiday.  
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='10'
                  onClick={() => toggleEventKey('10')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        Do your Professional House Cleaners accept tips?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='10'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='10'>
                  <Card.Body>
                    <p>
                      Our PHC’s are allowed to accept tips. While tipping is not expected it is always appreciated!
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='11'
                  onClick={() => toggleEventKey('11')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What if someone gets hurt in my home? 
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='11'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='11'>
                  <Card.Body>
                    <p>
                      Two Maids is fully licensed, bonded, and insured allowing you peace of mind. Any on the job injuries will be covered by the claim.
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card className='accordion__card'>
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey='12'
                  onClick={() => toggleEventKey('12')}
                >
                  <Row className='flex-nowrap'>
                    <Col xs={10} md={9}>
                      <h3 className='h4 font-weight-normal'>
                        What if something is broken during my cleaning?
                      </h3>
                    </Col>
                    <AccordionIcon
                      eventKey='12'
                      currentActiveKey={activeEventKey}
                      className='col d-flex justify-content-end'
                    />
                  </Row>
                </Accordion.Toggle>

                <Accordion.Collapse eventKey='12'>
                  <Card.Body>
                    <p>
                      At Two Maids, your home will be treated with the utmost care. In the event of an accident occurring, we will do our best to repair or replace 
                      and the damage we cause. All our PHC’s are fully licensed, bonded, and insured to protect you against any worse case scenarios.
                    </p>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          </Section>
          <Section />
        </Layout>
      )}
    </FranchiseeProvider>
  );
};

export default Faqs;
